import React from "react";
import { Row, Col, message, Tabs, Space } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { ReModal, ReButton } from "@/components/refineElement";
import { showDelConfirm, getOnOkFunction } from "@/lib/antdElementFunction";
import { timeHrMin, currentTime, getMoment, dateFormat, getStartTimeBeforeXDays } from "@/lib/dateTime";
import BusConfig from "@/config/bus";
import { getLangList } from '@/lib/i18n';
import { hasPermission } from "@/lib/permissionUtil";
import ModalBusTime from './ModalBusTime';
import Path from './Path';

const { getSchoolInfo, createBusTimeInfo, updateBusTimeInfo, deleteBusTimeInfo } = BusConfig;
const { TabPane } = Tabs;

interface props {
  allStudentArray: any;
  busTimeArray: any;
  getBusTimeInfo: any;
  activeDayKey: number;
  constActiveDayKey: number;
}

interface state {
  isModalVisible: boolean;
  handleType: string;
  pathArray: any;
  busTimeInfo: any;
  activeBusTimeId: string;
}

class BusTime extends React.Component<props, state> {
  state = {
    isModalVisible: false,
    handleType: '',
    pathArray: [] as never as any,
    busTimeInfo: {} as never as any,
    activeBusTimeId: '',
  };
  busT: any = getLangList("bus");
  errorT: any = getLangList("error");

  componentDidMount() {
    const { busTimeArray = [] } = this.props;
    const activeBusTimeId = `${busTimeArray?.[0]?.id ?? ''}`;

    if (activeBusTimeId) {
      busTimeArray.forEach((item: any) => {
        const { startTime, endTime, id } = item;
        const start = `${currentTime.format(dateFormat)} ${startTime}:00`;
        const end = `${currentTime.format(dateFormat)} ${endTime}:00`;

        if (currentTime.isBetween(getMoment(start), getMoment(end))) {
          this.setState({ activeBusTimeId: `${id}` });
          this.getSchoolInfo(id);
        }
      });
    }
  }

  getSchoolInfo = async (busTimeId: string = this.state.activeBusTimeId) => {
    const params = { busTimeId };

    return getSchoolInfo(params).then((res: any) => {
      const pathArray = res?.value ?? [];
      this.setState({ pathArray });
    });
  }

  toggleModal = (handleType: string = '') => {
    const { isModalVisible } = this.state;
    this.setState({ isModalVisible: !isModalVisible, handleType });
  }

  clickCreate = () => {
    this.setState({ busTimeInfo: {} }, () => this.toggleModal('create'));
  }

  clickEdit = (data: object) => (e: any) => {
    e.stopPropagation();
    this.setState({ busTimeInfo: data }, () => this.toggleModal('update'));
  }

  clickDelete = () => {
    const { activeBusTimeId } = this.state;
    showDelConfirm(() => this.deleteBusTimeInfo(activeBusTimeId));
  };

  createBusTimeInfo = (values: any) => {
    const { getBusTimeInfo } = this.props;
    const { busTime, busTimeReal, dayOfWeek } = values;
    const params = {
      busTime: busTime.format(timeHrMin),
      startTime: busTimeReal[0].format(timeHrMin),
      endTime: busTimeReal[1].format(timeHrMin),
      dayOfWeek,
    };

    createBusTimeInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.toggleModal();
      getBusTimeInfo();
    });
  }

  updateBusTimeInfo = (values: any) => {
    const { getBusTimeInfo } = this.props;
    const { busTimeInfo } = this.state;
    const { id } = busTimeInfo;
    const { busTime, busTimeReal, dayOfWeek } = values;
    const params = {
      id,
      busTime: busTime.format(timeHrMin),
      startTime: busTimeReal[0].format(timeHrMin),
      endTime: busTimeReal[1].format(timeHrMin),
      dayOfWeek,
    };

    if (!hasPermission('BUS_EDIT')) {
      return message.error(this.errorT.NO_PERMISSION);
    }

    updateBusTimeInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.toggleModal();
      getBusTimeInfo();
    });
  }

  deleteBusTimeInfo = (id: string) => {
    const { getBusTimeInfo, busTimeArray = [] } = this.props;
    const params = { busTimeId: id };

    deleteBusTimeInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.toggleModal();
      getBusTimeInfo().then(() => {
        this.setState({ activeBusTimeId: `${busTimeArray?.[0]?.id ?? ''}` });
      });
    });
  }

  renderSchoolTab = () => {
    const { busTimeArray = [], allStudentArray, activeDayKey, constActiveDayKey } = this.props;
    const { pathArray, activeBusTimeId } = this.state;
    const activeBusTimeObject = busTimeArray?.find((item: any) => `${item.id}` === activeBusTimeId) ?? {};
    const selectedDay = getStartTimeBeforeXDays(constActiveDayKey - activeDayKey);
    const activeTimeArray = [
      getMoment(`${selectedDay.format(dateFormat)} ${activeBusTimeObject?.startTime ?? '00:00'}:00`).valueOf(),
      getMoment(`${selectedDay.format(dateFormat)} ${activeBusTimeObject?.endTime ?? '00:00'}:00`).valueOf(),
    ];

    return busTimeArray.map((item: any) => {
      const { busTime, id, startTime, endTime } = item;
      const start = `${currentTime.format(dateFormat)} ${startTime}:00`;
      const end = `${currentTime.format(dateFormat)} ${endTime}:00`;
      const isShow = currentTime.isBetween(getMoment(start), getMoment(end)) || hasPermission(['BUS_EDIT', 'BUS_DELETE']);

      return (
        isShow
          ? (
            <TabPane
              key={id}
              tab={
                <Space>
                  <span>{busTime}</span>
                  {activeBusTimeId === id.toString() && hasPermission(['BUS_EDIT', 'BUS_DELETE']) &&
                    <span onClick={this.clickEdit(item)}><EditOutlined /></span>
                  }
                </Space>
              }
            >
              <Path
                allStudentArray={allStudentArray}
                pathArray={pathArray}
                activeBusTimeId={activeBusTimeId}
                getSchoolInfo={this.getSchoolInfo}
                activeDayKey={activeDayKey}
                constActiveDayKey={constActiveDayKey}
                activeTimeArray={activeTimeArray}
              />
            </TabPane >
          )
          : null
      );
    });
  }

  onChangeBustime = (activeKey: string) => {
    this.getSchoolInfo(activeKey).then(() => this.setState({ activeBusTimeId: activeKey }));
  }

  render() {
    const { activeDayKey } = this.props;
    const { isModalVisible, busTimeInfo, handleType, activeBusTimeId } = this.state;
    const okFunction = {
      create: this.createBusTimeInfo,
      update: this.updateBusTimeInfo,
    };

    return (
      <>
        <Row>
          <Col span={24}>
            <Tabs
              onChange={this.onChangeBustime}
              activeKey={activeBusTimeId}
              destroyInactiveTabPane
              tabBarExtraContent={hasPermission('BUS_NEW') &&
                <ReButton iconName='plus' onClick={this.clickCreate}>
                  {this.busT.NEW_BUS_TIME}
                </ReButton>
              }
            >
              {this.renderSchoolTab()}
            </Tabs>
          </Col>
        </Row>

        {isModalVisible &&
          <ReModal
            title={this.busT.TIME}
            onCancel={() => this.toggleModal()}
            visible={isModalVisible}
            onOk={(data: any) => getOnOkFunction(handleType, data, okFunction)}
            CollectedForm={ModalBusTime}
            formProps={{
              handleType,
              busTimeInfo,
              clickDelete: this.clickDelete,
              activeDayKey,
            }}
          />
        }
      </>
    );
  }
}

export default BusTime;